import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiArrowDownRight, FiArrowUpRight, FiDownload, FiMinus, FiRefreshCw } from 'react-icons/fi';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Button from '../../Components/UI/Button';
import { apiGet } from '../../Components/CommonComponents/tableApi';
import exportToExcel from '../../UtilityHookFunctions/exportToExcel';

/**
 * Medicine Rate Report.
 *
 * Purchase rates per medicine, compared across distributors (summary) and over
 * time (date-wise detail). Every change is measured against the previous
 * purchase of the same medicine from the same distributor. Orders raised before
 * rates were captured carry no rate and are left out.
 */

const EMPTY_FILTERS = { medicine: '', distributor: '', from: '', to: '' };

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const isNum = (value) => typeof value === 'number' && Number.isFinite(value);
const money = (value) => (isNum(value) ? value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—');
const pct = (value) => (isNum(value) ? `${value > 0 ? '+' : ''}${value.toFixed(2)}%` : '—');
const medicineLabel = (row) => (row?.potency ? `${row.medicine} (${row.potency})` : row?.medicine || '—');

const TREND_STYLES = {
  up: 'bg-rose-50 text-rose-700',
  down: 'bg-emerald-50 text-emerald-700',
  same: 'bg-slate-100 text-slate-600',
  first: 'bg-slate-50 text-slate-400',
};

const ROW_TINT = { up: 'bg-rose-50/40', down: 'bg-emerald-50/40' };

const ChangeBadge = ({ trend, change, changePct }) => {
  if (!trend || trend === 'first') {
    return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${TREND_STYLES.first}`}>First purchase</span>;
  }
  const Icon = trend === 'up' ? FiArrowUpRight : trend === 'down' ? FiArrowDownRight : FiMinus;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${TREND_STYLES[trend]}`}>
      <Icon className="h-3.5 w-3.5" />
      {trend === 'same' ? 'No change' : `${change > 0 ? '+' : ''}${money(change)} (${pct(changePct)})`}
    </span>
  );
};

const changeText = (row) =>
  !row?.trend || row.trend === 'first' ? 'First purchase' : row.trend === 'same' ? 'No change' : row.trend === 'up' ? 'Increase' : 'Decrease';

const SUMMARY_COLUMNS = [
  { key: 'medicine', label: 'Medicine' },
  { key: 'potency', label: 'Potency' },
  { key: 'distributor', label: 'Distributor' },
  { key: 'purchases', label: 'Purchases' },
  { key: 'totalQty', label: 'Total Qty' },
  { key: 'lastRate', label: 'Last Rate' },
  { key: 'lastDate', label: 'Last Purchase', format: (v) => formatDate(v) },
  { key: 'minRate', label: 'Min Rate' },
  { key: 'maxRate', label: 'Max Rate' },
  { key: 'avgRate', label: 'Avg Rate' },
  { key: 'prevRate', label: 'Previous Rate', format: (v) => (isNum(v) ? v : '—') },
  { key: 'change', label: 'Change', format: (v) => (isNum(v) ? v : '—') },
  { key: 'changePct', label: 'Change %', format: (v) => (isNum(v) ? v : '—') },
  { key: 'trend', label: 'Trend', format: (v, row) => changeText(row) },
  { key: 'mrp', label: 'MRP', format: (v) => (isNum(v) ? v : '—') },
  { key: 'marginPct', label: 'Margin on MRP %', format: (v) => (isNum(v) ? v : '—') },
  { key: 'isLowest', label: 'Lowest Rate', format: (v) => (v ? 'Yes' : '') },
];

const DETAIL_COLUMNS = [
  { key: 'date', label: 'Date', format: (v) => formatDate(v) },
  { key: 'orderId', label: 'Order Id' },
  { key: 'medicine', label: 'Medicine' },
  { key: 'potency', label: 'Potency' },
  { key: 'distributor', label: 'Distributor' },
  { key: 'qty', label: 'Qty' },
  { key: 'rate', label: 'Rate' },
  { key: 'amount', label: 'Amount' },
  { key: 'prevRate', label: 'Previous Rate', format: (v) => (isNum(v) ? v : '—') },
  { key: 'change', label: 'Change', format: (v) => (isNum(v) ? v : '—') },
  { key: 'changePct', label: 'Change %', format: (v) => (isNum(v) ? v : '—') },
  { key: 'trend', label: 'Trend', format: (v, row) => changeText(row) },
  { key: 'mrp', label: 'MRP', format: (v) => (isNum(v) ? v : '—') },
];

const th = 'px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap';
const thRight = `${th} text-right`;
const td = 'px-3 py-2 text-slate-700 whitespace-nowrap';
const tdRight = `${td} text-right tabular-nums`;
const field =
  'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-xs md:text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition';

export default function MedicineRateReport() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [onlyChanges, setOnlyChanges] = useState(false);

  const load = useCallback(async (query) => {
    setIsLoading(true);
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    const data = await apiGet(`inventory/reports/medicine-rates?${params.toString()}`);
    setReport(data || { rows: [], summary: [], options: {} });
    setIsLoading(false);
  }, []);

  useEffect(() => {
    load(applied);
  }, [applied, load]);

  const setField = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = (event) => {
    event?.preventDefault();
    if (filters.from && filters.to && filters.from > filters.to) {
      toast.error('From date must be before To date');
      return;
    }
    setApplied({ ...filters });
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
  };

  const summary = report?.summary || [];
  const rows = report?.rows || [];
  const detailRows = useMemo(
    () => (onlyChanges ? rows.filter((row) => row.trend === 'up' || row.trend === 'down') : rows),
    [rows, onlyChanges]
  );

  const stats = useMemo(
    () => ({
      medicines: new Set(summary.map((row) => `${row.medicine}|${row.potency}`)).size,
      distributors: new Set(summary.map((row) => row.distributor)).size,
      increases: rows.filter((row) => row.trend === 'up').length,
      decreases: rows.filter((row) => row.trend === 'down').length,
    }),
    [summary, rows]
  );

  const fileSuffix = [applied.medicine, applied.distributor, applied.from, applied.to].filter(Boolean).join('_');

  const exportDetail = () => {
    if (detailRows.length === 0) return toast.info('Nothing to export');
    exportToExcel(detailRows, `Medicine_Rate_Report${fileSuffix ? `_${fileSuffix}` : ''}`, DETAIL_COLUMNS, 'Date wise rates');
  };

  const exportSummary = () => {
    if (summary.length === 0) return toast.info('Nothing to export');
    exportToExcel(summary, `Medicine_Rate_Summary${fileSuffix ? `_${fileSuffix}` : ''}`, SUMMARY_COLUMNS, 'Distributor rates');
  };

  return (
    <div className="w-full space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-slate-900">Medicine Rate Report</h1>
          <p className="mt-0.5 text-xs text-slate-500">
            Purchase rates by distributor and date. Changes are compared with the previous purchase of the same medicine from the same distributor.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportSummary} disabled={isLoading || summary.length === 0}>
            <FiDownload className="h-4 w-4" />
            Export Summary
          </Button>
          <Button variant="success" size="sm" onClick={exportDetail} disabled={isLoading || detailRows.length === 0}>
            <FiDownload className="h-4 w-4" />
            Export to Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <form onSubmit={applyFilters} className="grid grid-cols-1 gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2 lg:grid-cols-5">
        <label className="text-xs font-medium text-slate-600">
          Medicine
          <input name="medicine" list="rate-report-medicines" value={filters.medicine} onChange={setField} placeholder="All medicines" className={`${field} mt-1`} />
          <datalist id="rate-report-medicines">
            {(report?.options?.medicines || []).map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
        </label>
        <label className="text-xs font-medium text-slate-600">
          Distributor
          <select name="distributor" value={filters.distributor} onChange={setField} className={`${field} mt-1`}>
            <option value="">All distributors</option>
            {(report?.options?.distributors || []).map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs font-medium text-slate-600">
          From
          <input type="date" name="from" value={filters.from} onChange={setField} className={`${field} mt-1`} />
        </label>
        <label className="text-xs font-medium text-slate-600">
          To
          <input type="date" name="to" value={filters.to} onChange={setField} className={`${field} mt-1`} />
        </label>
        <div className="flex items-end gap-2">
          <Button type="submit" size="md" className="flex-1">
            Apply
          </Button>
          <Button variant="outline" size="md" onClick={resetFilters} title="Clear filters">
            <FiRefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </form>

      {/* Quick stats */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          { label: 'Medicines', value: stats.medicines },
          { label: 'Distributors', value: stats.distributors },
          { label: 'Rate increases', value: stats.increases, tone: 'text-rose-600' },
          { label: 'Rate decreases', value: stats.decreases, tone: 'text-emerald-600' },
        ].map((item) => (
          <div key={item.label} className="rounded-xl border border-slate-200 bg-white px-4 py-3">
            <p className="text-xs text-slate-500">{item.label}</p>
            <p className={`text-lg font-semibold ${item.tone || 'text-slate-900'}`}>{isLoading ? '…' : item.value}</p>
          </div>
        ))}
      </div>

      {report?.unratedCount > 0 && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
          {report.unratedCount} order line{report.unratedCount === 1 ? '' : 's'} in this range have no rate recorded (raised before rates were captured) and are not included.
        </p>
      )}

      {/* Summary: medicine x distributor */}
      <section className="rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-slate-900">Rates by Distributor</h2>
          <p className="text-xs text-slate-500">Last, lowest, highest and average purchase rate of each medicine from each distributor in the selected period.</p>
        </div>
        <div className="max-h-[480px] overflow-auto">
          <table className="w-full min-w-[1100px] text-sm">
            <thead className="sticky top-0 bg-slate-50">
              <tr>
                <th className={th}>Medicine</th>
                <th className={th}>Distributor</th>
                <th className={thRight}>Purchases</th>
                <th className={thRight}>Last Rate</th>
                <th className={th}>Last Purchase</th>
                <th className={thRight}>Min</th>
                <th className={thRight}>Max</th>
                <th className={thRight}>Avg</th>
                <th className={th}>Change vs Previous</th>
                <th className={thRight}>MRP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr>
                  <td colSpan={10} className="px-3 py-8 text-center text-slate-400">Loading…</td>
                </tr>
              ) : summary.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-3 py-8 text-center text-slate-400">No purchase rates found for these filters.</td>
                </tr>
              ) : (
                summary.map((row) => (
                  <tr key={row.key} className={ROW_TINT[row.trend] || ''}>
                    <td className={`${td} font-medium text-slate-900`}>{medicineLabel(row)}</td>
                    <td className={td}>
                      {row.distributor}
                      {row.isLowest && (
                        <span className="ml-2 rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700">Lowest</span>
                      )}
                    </td>
                    <td className={tdRight}>{row.purchases}</td>
                    <td className={`${tdRight} font-semibold text-slate-900`}>{money(row.lastRate)}</td>
                    <td className={td}>{formatDate(row.lastDate)}</td>
                    <td className={tdRight}>{money(row.minRate)}</td>
                    <td className={tdRight}>{money(row.maxRate)}</td>
                    <td className={tdRight}>{money(row.avgRate)}</td>
                    <td className={td}>
                      <ChangeBadge trend={row.trend} change={row.change} changePct={row.changePct} />
                    </td>
                    <td className={tdRight}>{money(row.mrp)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Date-wise detail */}
      <section className="rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">Date-wise Purchases</h2>
            <p className="text-xs text-slate-500">
              Newest first. Red rows cost more than the previous purchase, green rows cost less.
              {report?.truncated ? ` Showing the latest ${rows.length} of ${report.total}; narrow the filters to see more.` : ''}
            </p>
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" className="h-4 w-4 rounded accent-sky-600" checked={onlyChanges} onChange={(e) => setOnlyChanges(e.target.checked)} />
            Only rate changes
          </label>
        </div>
        <div className="max-h-[560px] overflow-auto">
          <table className="w-full min-w-[1100px] text-sm">
            <thead className="sticky top-0 bg-slate-50">
              <tr>
                <th className={th}>Date</th>
                <th className={th}>Order Id</th>
                <th className={th}>Medicine</th>
                <th className={th}>Distributor</th>
                <th className={thRight}>Qty</th>
                <th className={thRight}>Rate</th>
                <th className={thRight}>Previous</th>
                <th className={th}>Change</th>
                <th className={thRight}>Amount</th>
                <th className={thRight}>MRP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr>
                  <td colSpan={10} className="px-3 py-8 text-center text-slate-400">Loading…</td>
                </tr>
              ) : detailRows.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-3 py-8 text-center text-slate-400">No purchases found for these filters.</td>
                </tr>
              ) : (
                detailRows.map((row, index) => (
                  <tr key={`${row.orderId}-${row.medicine}-${row.potency}-${index}`} className={ROW_TINT[row.trend] || ''}>
                    <td className={td}>{formatDate(row.date)}</td>
                    <td className={td}>{row.orderId || '—'}</td>
                    <td className={`${td} font-medium text-slate-900`}>{medicineLabel(row)}</td>
                    <td className={td}>{row.distributor}</td>
                    <td className={tdRight}>{row.qty ?? '—'}</td>
                    <td className={`${tdRight} font-semibold text-slate-900`}>{money(row.rate)}</td>
                    <td className={tdRight}>{money(row.prevRate)}</td>
                    <td className={td}>
                      <ChangeBadge trend={row.trend} change={row.change} changePct={row.changePct} />
                    </td>
                    <td className={tdRight}>{money(row.amount)}</td>
                    <td className={tdRight}>{money(row.mrp)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <ToastContainer />
    </div>
  );
}
